import { useState, useEffect } from 'react';
import { Button } from '@/components/common';
import { useAuthStore } from '@/store';
import { apiClient } from '@/services/api';
import '@/pages/onboarding/onboarding.css';

/**
 * Marketing consent modal — DB-driven, works cross-device.
 *
 * Shows when:
 *  - User is authenticated
 *  - user.marketingConsent !== true  (hasn't opted in yet)
 *  - user.marketingConsentAsked !== true  (hasn't been asked yet)
 *
 * On accept: PATCH consent=true → update store → modal hides forever.
 * On decline: PATCH consent=false → update store → modal hides forever
 *             (marketingConsentAsked becomes true in DB).
 */
export function MarketingConsentModal() {
  const { isAuthenticated, user, updateUser } = useAuthStore();
  const [visible, setVisible] = useState(false);
  const [optedIn, setOptedIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    // Already consented or already asked — don't show
    if (user.marketingConsent || user.marketingConsentAsked) return;

    // Small delay to avoid flash on page load
    const timer = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(timer);
  }, [isAuthenticated, user]);

  if (!visible) return null;

  const handleDismiss = async () => {
    setSubmitting(true);

    try {
      // Send actual choice to backend (true or false)
      await apiClient.patch('/api/v1/auth/marketing-consent', { consent: optedIn });
      // Update auth store so modal never shows again in this session
      updateUser({ marketingConsent: optedIn, marketingConsentAsked: true });
    } catch (err) {
      console.error('[ConsentModal] Failed to update consent:', err);
      // On error — don't update store, modal will re-appear next visit
    }

    setSubmitting(false);
    setVisible(false);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.5)',
        padding: 'var(--space-4)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleDismiss();
      }}
    >
      <div
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--space-8)',
          maxWidth: '420px',
          width: '100%',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        {/* Illustration */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-4)' }}>
          <svg width="48" height="48" viewBox="0 0 64 64" fill="none" aria-hidden="true">
            <rect x="8" y="16" width="48" height="36" rx="4" stroke="var(--color-primary)" strokeWidth="2" fill="color-mix(in srgb, var(--color-primary) 10%, transparent)" />
            <path d="M8 20l24 16 24-16" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <h3
          style={{
            fontSize: '1.25rem',
            fontWeight: 700,
            color: 'var(--color-text)',
            textAlign: 'center',
            marginBottom: 'var(--space-2)',
          }}
        >
          Get Weekly Job Search Tips
        </h3>
        <p
          style={{
            fontSize: '0.875rem',
            color: 'var(--color-text-secondary)',
            textAlign: 'center',
            marginBottom: 'var(--space-4)',
            lineHeight: 1.5,
          }}
        >
          ATS score tips, interview prep guides, and new features. About 2 emails per month.
        </p>

        {/* Toggle */}
        <div
          className={`consent-toggle${optedIn ? ' consent-toggle--active' : ''}`}
          onClick={() => setOptedIn(!optedIn)}
          role="switch"
          aria-checked={optedIn}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOptedIn(!optedIn);
            }
          }}
        >
          <div className={`consent-toggle__track${optedIn ? ' consent-toggle__track--on' : ''}`}>
            <div className={`consent-toggle__thumb${optedIn ? ' consent-toggle__thumb--on' : ''}`} />
          </div>
          <div>
            <div className="consent-toggle__label">Send me tips and updates</div>
            <div className="consent-toggle__sublabel">
              {user?.email || 'Your email'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
          <Button onClick={handleDismiss} isLoading={submitting} fullWidth>
            {optedIn ? 'Subscribe & Continue' : 'Continue'}
          </Button>
        </div>

        <p
          style={{
            fontSize: '0.6875rem',
            color: 'var(--color-text-muted)',
            textAlign: 'center',
            marginTop: 'var(--space-3)',
          }}
        >
          Unsubscribe anytime.{' '}
          <a href="/privacy" target="_blank" rel="noopener" style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
}
